/**
 * Browser-only session state and the one fetch wrapper every API call goes through.
 *
 * The access token (a 15 minute JWT) lives in the module-level variables below and nowhere else:
 * never in localStorage, and never touched during a server render. The site is rendered on the
 * server (web/server.js), which does not use httpService, so nothing in here may run outside the
 * browser. The only code that sets the token is the login/refresh flow, which runs in event
 * handlers and effects.
 *
 * The longer-lived credential is the httpOnly `refresh_token` cookie the API sets. JavaScript
 * cannot see it. A same-origin fetch sends it by default, so the refresh call needs no extra
 * options.
 */

/** Refresh ahead of time when the access token expires within this many milliseconds. */
const REFRESH_MARGIN_MS = 30_000;

let accessToken = null;
/** Epoch milliseconds at which the access token stops being valid. */
let expiresAt = 0;
/**
 * False for the token handed out before the email is verified: the API will not refresh it
 * (refresh answers 204 until the OTP step is done), so there is nothing to try on a 401.
 */
let refreshable = false;
/**
 * Bumped whenever the app itself changes the session: a login, a logout, a pending token coming or
 * going. A refresh remembers the value it started under and only applies its answer if the value is
 * still the same, so a slow refresh can never undo a logout or a newer login. The refresh's own
 * changes to the session do not bump it.
 */
let generation = 0;
/** @type {{ generation: number, promise: Promise<object|null> }|null} */
let refreshInFlight = null;
const sessionListeners = new Set();

function notifySessionListeners(session) {
  for (const listener of sessionListeners) {
    listener(session);
  }
}

function storeToken({ jwt, expiresIn, verified }) {
  accessToken = jwt;
  // Without a usable expiresIn, never refresh ahead of time and lean on the 401 path.
  expiresAt = Number.isFinite(expiresIn) ? Date.now() + expiresIn * 1000 : Number.POSITIVE_INFINITY;
  refreshable = verified !== false;
}

/** Install a session and tell subscribers, without invalidating refreshes already under way. */
function applySession(authBody) {
  storeToken(authBody);
  notifySessionListeners(authBody);
}

/** End the session and tell subscribers, without invalidating refreshes already under way. */
function endSession() {
  accessToken = null;
  expiresAt = 0;
  refreshable = false;
  notifySessionListeners(null);
}

/** Forget the token of an unverified account, if that is what is held. Nobody hears about it. */
function dropPendingToken() {
  accessToken = null;
  expiresAt = 0;
}

const holdsPendingToken = () => accessToken !== null && !refreshable;

/**
 * Be told whenever the session changes: called with the AuthBody after a login or a refresh and
 * with null when the session is cleared. UserContext uses this to keep its state in step with
 * refreshes that happen inside apiFetch.
 * @param {(session: object|null) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeToSession(listener) {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

/**
 * Remember the token without telling the app a user is logged in. Used for the token that comes
 * with an unverified account, which is only good for verifying the OTP code.
 * @param {{ jwt: string, expiresIn: number, verified?: boolean }} authBody
 */
export function setPendingToken(authBody) {
  generation += 1;
  storeToken(authBody);
}

/**
 * Store the access token of a logged-in user and notify subscribers.
 * @param {{ jwt: string, email: string, roles: string[], verified: boolean, expiresIn: number }} authBody
 */
export function setSession(authBody) {
  generation += 1;
  applySession(authBody);
}

/** Forget the access token and notify subscribers. */
export function clearSession() {
  generation += 1;
  endSession();
}

/**
 * Drop the token handed out before the email was verified, if that is what is held. A real
 * session is left alone, so this is safe to call after a login has completed.
 */
export function discardPendingToken() {
  if (holdsPendingToken()) {
    generation += 1;
    dropPendingToken();
  }
}

/** @returns {string|null} the current access token, for callers that cannot use apiFetch. */
export function getAccessToken() {
  return accessToken;
}

/**
 * Make sure the access token is good for at least the refresh margin, refreshing if it is not.
 * A pending token (an unverified account's) cannot be refreshed, so one that has expired is dropped
 * instead: the API turns down an expired token on every endpoint, public ones included.
 * For code that hands the token to something other than fetch, such as the PDF renderer.
 * @returns {Promise<string|null>} the current access token, or null when there is no session.
 */
export async function ensureFreshAccessToken() {
  if (holdsPendingToken() && expiresAt <= Date.now()) {
    dropPendingToken();
  } else if (accessToken && refreshable && expiresAt - Date.now() < REFRESH_MARGIN_MS) {
    try {
      await refreshSession();
    } catch {
      // The refresh failed. Whether that ended the session or only was not answered (network, 5xx),
      // carry on with whatever token is held now.
    }
  }
  return accessToken;
}

async function requestRefresh(startedUnder) {
  // The answer only counts if nothing changed the session while the request was out.
  const stillCurrent = () => startedUnder === generation;

  let response;
  try {
    response = await fetch("/api/users/refresh", { method: "POST" });
  } catch (error) {
    // Not getting an answer says nothing about the session, so it is left as it is.
    throw { status: 0, error };
  }

  // 204 is the API's "no session": no cookie, or one that is unknown, revoked, expired or not yet
  // verified. It is the normal state of an anonymous visitor, not a failure. (It is a 204 rather
  // than a 401 so the browser does not log a failed request on every public page load.)
  if (response.status === 204) {
    if (stillCurrent()) endSession();
    return null;
  }

  if (!response.ok) {
    // Only a definitive answer (a 4xx) ends the session. A 5xx, such as the 502 while the API
    // restarts during a deploy, says nothing about it: the access token and the cookie may both
    // still be good, and the next call tries again.
    const definitive = response.status >= 400 && response.status < 500;
    if (definitive && stillCurrent()) endSession();
    throw { status: response.status, error: Error("Not authorized") };
  }

  const authBody = await response.json();
  if (!stillCurrent()) return null;

  applySession(authBody);
  return authBody;
}

/**
 * Exchange the refresh cookie for a new access token. Callers that arrive while a refresh is
 * running share it: the API answers each with the same cookie, but there is no reason to ask twice.
 * When there is no session the session is cleared (subscribers get null) and this resolves to null.
 * A 4xx clears the session too, and the rejection is `{ status, error }`. A network error or a 5xx
 * rejects the same way but leaves the session alone: it is not an answer about the session.
 *
 * If the session is changed by a login, a logout or a pending token while the request is out, the
 * answer is dropped and the session is left as that change made it; a successful answer then
 * resolves to null. A caller never joins a refresh that began before such a change.
 * @returns {Promise<{ jwt: string, email: string, roles: string[], verified: boolean, expiresIn: number }|null>}
 */
export function refreshSession() {
  if (!refreshInFlight || refreshInFlight.generation !== generation) {
    const startedUnder = generation;
    const promise = requestRefresh(startedUnder).finally(() => {
      if (refreshInFlight?.promise === promise) {
        refreshInFlight = null;
      }
    });
    refreshInFlight = { generation: startedUnder, promise };
  }
  return refreshInFlight.promise;
}

function isFormData(body) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function send(path, options) {
  const headers = new Headers(options.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (isFormData(options.body)) {
    // The browser has to write multipart/form-data itself, boundary included.
    headers.delete("Content-Type");
  }
  return fetch(path, { ...options, headers });
}

/**
 * fetch() with the session attached.
 *
 * - Sends `Authorization: Bearer <token>` when there is a token, and refreshes first when it
 *   expires within 30 seconds.
 * - A 401 refreshes once and retries once. So does a 403, which picks up a role granted moments ago.
 * - A pending token (an unverified account's) cannot be refreshed. An expired one is dropped before
 *   the request goes out; if one is rejected with a 401 anyway it is dropped and the request is
 *   retried once without a token, so a stale pending token never breaks public endpoints.
 * - If the refresh finds no session or is turned down (a 4xx), the session is cleared (subscribers are
 *   told) and the request is retried once without a token, so public endpoints keep working for a
 *   visitor whose session ended. If the refresh could not be answered (network, 5xx) the session is
 *   kept and the request is retried with the token still held.
 * - Concurrent callers share one refresh.
 *
 * Resolves to the final Response whatever its status; callers check `response.ok` as before.
 * @param {string} path
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function apiFetch(path, options = {}) {
  await ensureFreshAccessToken();

  const tokenUsed = accessToken;
  const usedPendingToken = holdsPendingToken();
  const response = await send(path, options);

  const rejected = response.status === 401 || response.status === 403;
  if (!rejected) {
    return response;
  }

  if (usedPendingToken) {
    // There is nothing to refresh. A 403 means this account may not do that, and asking again
    // without the token would not change it. A 401 means the API no longer accepts the token (it
    // has expired), and it turns such a token down on every endpoint, public ones too. So drop it
    // and ask again as an anonymous visitor.
    if (response.status === 403) {
      return response;
    }
    if (accessToken === tokenUsed) {
      dropPendingToken();
    }
    return send(path, options);
  }

  // Another request may have refreshed (or ended) the session while this one was in flight; then
  // there is nothing left to refresh and the retry just picks up whatever the session is now.
  if (accessToken === tokenUsed) {
    try {
      await refreshSession();
    } catch {
      // The refresh was turned down and cleared the session, so the retry below goes out
      // anonymously, as it does when the refresh resolves to null (no session). Or it could not be
      // answered (network, 5xx), and the retry goes out with the token still held.
    }
  }
  return send(path, options);
}
